import {fetchWithTimeout} from "../../util/helperFunctions";
import moment from "moment/moment";
import uuid from "uuid";
import {addOrUpdate, getDB} from "../../util/indexedDBConnector";
import CTR from "../../util/receiptTemplate";
import {
    addErrorAlert,
    addProduct, changePaymentMode,
    findProduct,
    setLoading,
    showDialogue
} from "../../redux/actions";
import {addBlockedTransactionPaymentTypes} from "../../redux/payment/actions";
import {getGiftCardPaymentReasonCodes} from "../../redux/payment/selectors";

export const getPenniesConf = (state) => {
    let conf = window?.AppConf?.pennies || {};
    if(
        typeof conf.url === "undefined" ||
        typeof conf.token === "undefined" ||
        typeof conf.item === "undefined" ||
        typeof conf.merchantID === "undefined"
    ){
        return false;
    }
    if(conf?.useProxy){
        conf.mainUrl = state.rootReducer.erplyServiceEndpoints.goerp.url.split("/api")[0] + "/int/pennies-sync/proxy";
        conf.clientCode = state.rootReducer.user.clientCode;
        conf.sessionKey = state.rootReducer.user.sessionKey;
    }else{
        conf.mainUrl = conf.url;
    }
    return conf;
}

export const getStoreID = (state) => {
    return getPenniesConf(state)?.storeID ?? state.rootReducer.warehouse.code
}

export const addPenniesItem = (dispatch, state, donationAmount) => {
    return new Promise((resolve, reject) => {
        let conf = getPenniesConf(state);
        if(conf === false){
            reject("No pennies conf");
            return;
        }

        dispatch(setLoading(true));
        dispatch(findProduct(conf.item, state.rootReducer.warehouse.warehouseID, state.rootReducer.pos.pointOfSaleID, donationAmount, (product) => {
            let giftCardMOPCodes = getGiftCardPaymentReasonCodes(state.payment.paymentTypes);
            dispatch(addBlockedTransactionPaymentTypes("pennies", giftCardMOPCodes, (state) => {
                return new Promise((resolve, reject) => {
                    if(typeof state.rootReducer.productsInBasket.find(el => el.data.code === conf.item) !== "undefined"){
                        dispatch(showDialogue(
                            "Can't use this payment method",
                            "Gift card payment not allowed with a donation",
                            "Change payment",
                            () => {
                                resolve();
                                dispatch(changePaymentMode('options'));
                            },
                            undefined, undefined, false, undefined, undefined, true
                        ));
                    }else{
                        reject();
                    }
                });
            }));

            setTimeout(resolve, 1);
            dispatch(setLoading(false));
            product.setNotDiscountable();
            product.setAmountNotEditable();
            return addProduct(product);
        }, (code, error) => {
            reject();
            dispatch(setLoading(false));
            return addErrorAlert("Could not find charity item, please check the configuration!");
        }));
    })
}

export const onTransactionSuccess = (state) => {
    let pennies = state.rootReducer.pennies;
    if(pennies.added !== false){
        let dateTime = moment();
        dateTime.tz(state.rootReducer.erplyConf.timezone);
        let id = CTR.lastReceiptNumber !== false ? CTR.lastReceiptNumber : uuid.v4();
        API.donation(dateTime.format("YYYY-MM-DD\THH:mm:ss"), pennies.added, getStoreID(state), id, state).then((response) => {
            console.log("Pennies donation success", {response});
        }, ({requestData, response}) => {
            console.log("Pennies donation failed", {response});
            getDB(state.rootReducer.user.clientCode).then(() => {
                addOrUpdate("saveIntegrationLog", {
                    application: "pennies",
                    requestData,
                    retries: 0,
                    status: 0
                }).then(() => {}, (e) => {
                    console.log('addOrUpdate record failed', {requestData, e});
                });
            })
        });
    }
}

export const onDayClose = (state) => {
    let count = localStorage.getItem('penniesDonationAsked');
    if(count === null){
        count = 0;
    }
    if(count > 0){
        API.report(count, state).then(() => {
            localStorage.setItem('penniesDonationAsked', 0);
        }, () => {});
    }
}

const API = {
    sendRequest: (state, method, request, urlParams, data, timeout, headers={}, merchantIDInUrl=true) => {
        return new Promise((resolve, reject) => {
            let conf = getPenniesConf(state);
            if (conf === false) {
                reject("Pennies conf missing");
                return;
            }

            let requestParams = {
                method,
                headers: {
                    "Access-token": conf.token,
                    ...headers
                },
                timeout: (timeout ?? (conf?.timeout ?? 5)) * 1000
            };

            const urlSearchParams = new URLSearchParams({
                format: "json",
                ...urlParams
            });

            if(method === "POST"){
                requestParams.body = new URLSearchParams(data);
                requestParams.headers["Content-Type"] = "application/x-www-form-urlencoded"
            }

            let url = conf.url + (conf.url.endsWith("/") ? "" : "/");
            let merchantIDPart = merchantIDInUrl ? `/${conf.merchantID}` : "";
            let requestUrl = `${url}${request}${merchantIDPart}?${urlSearchParams.toString()}`;
            if(conf?.useProxy){
                requestUrl = `${conf.mainUrl}?u=${btoa(requestUrl)}`;
                let proxyRequestBody = {
                    method: requestParams.method,
                    headers: requestParams.headers
                }
                if(requestParams.method === "POST"){
                    proxyRequestBody.body = requestParams.body.toString();
                }
                requestParams.body = JSON.stringify(proxyRequestBody);
                requestParams.method = "POST";
                requestParams.headers = {
                    clientCode: conf.clientCode,
                    sessionKey: conf.sessionKey
                }
            }
            fetchWithTimeout(requestUrl, requestParams).then((response) => {
                response.json().then((result) => {
                    if(response.status === 200 || (response.status === 409 && result.error === "duplicate")){
                        resolve(result);
                    }else{
                        reject(result);
                    }
                }, () => {
                    reject("API error");
                });

            }, reject);
        });
    },
    calculation: (amount, storeID, state) => {
        return new Promise((resolve, reject) => {
            API.sendRequest(state, "GET", "donation/calculation", {amount, store_id: storeID}).then(resolve, reject);
        });
    },
    donation: (dateTime, amount, storeID, transactionID, state, retry = 0) => {
        return new Promise((resolve, reject) => {
            let urlParams = {};
            if(retry > 0){
                urlParams.retry = "true";
            }
            let requestData = {
                donation_date_time: dateTime,
                amount,
                store_id: storeID,
                transaction_id: transactionID
            };

            API.sendRequest(state, "POST", "donation", urlParams, requestData, 10).then(resolve, (response) => {
                if(retry < 3){
                    API.donation(dateTime, amount, storeID, transactionID, state, retry + 1).then(resolve, reject);
                }else{
                    reject({requestData, response});
                }
            });
        });
    },
    report: (donationAskedCount, state) => {
        let conf = getPenniesConf(state);
        let merchantID = conf?.merchantID ?? "";
        let dateTime = moment();
        let storeID = getStoreID(state);
        return API.sendRequest(state, "POST", "ped", {}, {
            merchant_id: merchantID,
            store_id: storeID,
            tid: state.rootReducer.pos.name,
            report_datetime: dateTime.utc().format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
            payments_count: donationAskedCount
        }, 10, {
            "Access-Account": merchantID,
        }, false);
    }
}

export default API;